import Fuse from "fuse.js";

import { isStatOrUrgentPriority } from "@/shared/models/referral";
import type { SpecialCaseConfig } from "@/src/lib/special-cases/types";
import { isMinorDateOfBirth } from "@/src/lib/utils/date-and-phone-utils";
import { buildKeywordRegex, textMatchesUnnegated } from "@/src/lib/utils/negated-keyword-match";
import { ServiceLine } from "@/src/models/constants/service-lines";
import { CoverageType } from "@/src/models/coverage";
import { DiagnosisItem, Referral } from "@/src/models/documents/referral";
import { Patient } from "@/src/models/patient";

const CARDIAC_CLEARANCE_RE =
  /\b(cardiac\s+clearance|pre[\s-]*op(?:erative)?\s+clearance|surgical\s+clearance|clearance\s+for|clearance\s+prior|cardio(?:vascular)?\s+clearance)\b/i;

/**
 * Matches imaging/testing only when it appears in an ordering context (not merely mentioned).
 * Verb-first: (order|refer|request|schedule|need|require|perform) ... imaging_term
 * Term-first: imaging_term ... (ordered|requested|needed|required|scheduled|recommended)
 */
const IMAGING_TESTING_ORDER_RE =
  /\b(?:order(?:s|ed|ing)?(?:\s+for)?|refer(?:red)?(?:\s+for)?|request(?:ed|ing)?|schedule(?:d)?|need(?:s|ed)?|require[sd]?|perform(?:ed)?)\b[^.\n]{0,60}\b(?:echo(?:cardiogram)?|stress\s+test|holter|ekg|ecg|ct\s+scan|mri|x[\s-]*ray|nuclear\s+medicine|ultrasound|duplex|doppler|abi|angiogra(?:m|phy)|cath(?:eterization)?|mcot|zio|event\s+monitor)\b|\b(?:echo(?:cardiogram)?|stress\s+test|holter|ekg|ecg|ct\s+scan|mri|x[\s-]*ray|nuclear\s+medicine|ultrasound|duplex|doppler|abi|angiogra(?:m|phy)|cath(?:eterization)?|mcot|zio|event\s+monitor)\b[^.\n]{0,40}\b(?:ordered|requested|needed|required|scheduled|recommended)\b/i;

const INSURANCE_FUZZY_THRESHOLD = 0.2;

const BLANK_EQUIVALENT_LANGUAGES = new Set(["", "not provided", "not specified", "unknown"]);

export function isBlankEnglishOrSpanish(language: string | null | undefined): boolean {
  const normalized = (language ?? "").toLowerCase().trim();

  return BLANK_EQUIVALENT_LANGUAGES.has(normalized) || normalized === "english" || normalized === "spanish";
}

/** Returns triggered special-case labels for a referral + patient against the group's config. */
export function evaluateSpecialCases(referral: Referral, patient: Patient, config: SpecialCaseConfig): string[] {
  const triggered: string[] = [];

  // Only primary coverage is checked for insurance blacklist/consult — secondary is irrelevant.
  const payerName = referral.primaryCoverage?.payer ?? "";
  const primarySubscriberId = referral.primaryCoverage?.subscriberId ?? "";
  const subscriberIdHasLetterPrefix = /^[a-zA-Z]/.test(primarySubscriberId);
  const planNameHasCvs = /\bcvs\b/i.test(referral.primaryCoverage?.plan ?? "");
  const reasonForReferral = referral.reasonForReferral ?? "";
  const diagnosisList = referral.diagnosisList ?? [];
  // Groups that compose the diagnosis list into reasonForReferral (PIMA) scan a reason with those
  // diagnosis segments removed for the reason-only checks below, so appended diagnosis text cannot
  // false-trigger them; diagnosisList still drives every diagnosis-based rule.
  const reasonForOrderChecks = config.excludeDiagnosisTextFromReasonChecks
    ? reasonWithoutDiagnosisDescriptions(reasonForReferral, diagnosisList)
    : reasonForReferral;

  const isBlacklisted =
    (payerName && config.insuranceBlacklist.length > 0 && matchesInsuranceList(payerName, config.insuranceBlacklist)) ||
    (payerName &&
      config.insuranceBlacklistWithLetterIdPrefix.length > 0 &&
      subscriberIdHasLetterPrefix &&
      matchesInsuranceList(payerName, config.insuranceBlacklistWithLetterIdPrefix)) ||
    (payerName &&
      config.insuranceBlacklistWithCvsPlanName.length > 0 &&
      planNameHasCvs &&
      matchesInsuranceList(payerName, config.insuranceBlacklistWithCvsPlanName));

  if (isBlacklisted) {
    triggered.push("Insurance Blacklisted");
  }

  if (
    payerName &&
    config.insuranceConsultNeeded.length > 0 &&
    matchesInsuranceList(payerName, config.insuranceConsultNeeded)
  ) {
    triggered.push("Insurance Consult Needed");
  }

  if (CARDIAC_CLEARANCE_RE.test(reasonForOrderChecks)) {
    triggered.push("Cardiac Clearance");
  }

  if (isStatOrUrgentPriority(referral.schedulingPriority)) {
    triggered.push("STAT/Urgent");
  }

  if (referral.serviceLine === ServiceLine.VASCULAR) {
    triggered.push("Vascular");
  }

  if (referral.primaryCoverage?.coverageType === CoverageType.SELF_PAY) {
    triggered.push("Self Pay");
  }

  for (const rule of config.specialDiagnoses) {
    if (matchesDiagnosisRule(diagnosisList, reasonForReferral, rule.icdPrefixes ?? [], rule.keywords ?? [])) {
      triggered.push(rule.label);
    }
  }

  if (IMAGING_TESTING_ORDER_RE.test(reasonForOrderChecks)) {
    triggered.push("Imaging/Testing");
  }

  if (!isBlankEnglishOrSpanish(patient.language)) {
    triggered.push("Non English");
  }

  if (isMinorDateOfBirth(patient.dateOfBirth)) {
    triggered.push("Minor");
  }

  return triggered;
}

/**
 * Drop any ";"-delimited segment of the reason that is just a diagnosis description, so the
 * reason-only checks judge the actual reason rather than diagnoses composed into the field (PIMA).
 * Matching is normalized (lowercase, punctuation collapsed). diagnosisList still drives all
 * diagnosis-based rules, so this only removes text the reason-only regexes should not be judging.
 */
function reasonWithoutDiagnosisDescriptions(reason: string, diagnosisList: DiagnosisItem[]): string {
  const normalize = (text: string) =>
    text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, " ")
      .trim();
  const diagnosisTexts = new Set(
    diagnosisList.map((dx) => normalize(dx.diagnosisText ?? "")).filter((text) => text.length > 0)
  );

  if (diagnosisTexts.size === 0) return reason;

  return reason
    .split(";")
    .map((segment) => segment.trim())
    .filter((segment) => segment.length > 0 && !diagnosisTexts.has(normalize(segment)))
    .join("; ");
}

/** Lowercase; collapse punctuation/slashes/hyphens to spaces. */
function normalizeInsuranceName(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function compactInsuranceName(normalized: string): string {
  return normalized.replace(/\s+/g, "");
}

/** Substring/compact match, then one Fuse search over the list for typos. */
/** Exported for one-off payer-name checks outside the `SpecialCaseConfig` pipeline (e.g. TPR's
 * special-case insurance flag) that want the same typo-tolerant matching without adopting the
 * full `evaluateSpecialCases` config shape. */
export function matchesInsuranceList(value: string, list: string[]): boolean {
  const normalizedValue = normalizeInsuranceName(value);

  if (!normalizedValue) {
    return false;
  }

  const compactValue = compactInsuranceName(normalizedValue);
  const normalizedEntries: string[] = [];

  for (const entry of list) {
    const normalizedEntry = normalizeInsuranceName(entry);

    if (!normalizedEntry) {
      continue;
    }

    normalizedEntries.push(normalizedEntry);

    const compactEntry = compactInsuranceName(normalizedEntry);

    if (normalizedValue.includes(normalizedEntry) || compactValue.includes(compactEntry)) {
      return true;
    }
  }

  if (normalizedEntries.length === 0) {
    return false;
  }

  return new Fuse(normalizedEntries, {
    includeScore: true,
    threshold: INSURANCE_FUZZY_THRESHOLD,
    ignoreLocation: true,
    isCaseSensitive: false,
  })
    .search(normalizedValue)
    .some((hit) => {
      if ((hit.score ?? 1) > INSURANCE_FUZZY_THRESHOLD) return false;

      return normalizedValue.length >= hit.item.length - 2;
    });
}

function matchesDiagnosisRule(
  diagnosisList: DiagnosisItem[],
  reasonForReferral: string,
  icdPrefixes: string[],
  keywords: string[]
): boolean {
  if (icdPrefixes.length > 0) {
    for (const dx of diagnosisList) {
      const code = (dx.diagnosisCode ?? "").toUpperCase().replace(".", "");

      if (icdPrefixes.some((prefix) => code.startsWith(prefix.toUpperCase()))) {
        return true;
      }
    }
  }

  if (keywords.length > 0) {
    const keywordRegex = buildKeywordRegex(keywords);

    if (diagnosisList.some((dx) => textMatchesUnnegated(dx.diagnosisText, keywordRegex))) {
      return true;
    }

    if (textMatchesUnnegated(reasonForReferral, keywordRegex)) {
      return true;
    }
  }

  return false;
}
